import { useState, useMemo } from "react";

import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import CartSidebar from "@/components/feature/CartSidebar";
import ProductCard from "@/components/base/ProductCard";
import { useProducts } from "@/hooks/useProducts";

const SORT_OPTIONS = [
  { label: "Featured", value: "featured" },
  { label: "Price: Low to High", value: "price_asc" },
  { label: "Price: High to Low", value: "price_desc" },
  { label: "Newest First", value: "newest" },
  { label: "Best Rated", value: "rating" },
];

const FABRICS = ["Luminous Satin", "Pearl Satin", "Metallic Satin", "Iridescent Sequin", "Pleated Metallic", "Luminous Velvet"];
const SIZES = ["XS", "S", "M", "L", "XL"];

export default function ClothesPage() {
  const [sortBy, setSortBy] = useState("featured");
  const [selectedFabrics, setSelectedFabrics] = useState<string[]>([]);
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [priceMax, setPriceMax] = useState(200);
  const [filterOpen, setFilterOpen] = useState(false);

  const toggleFabric = (f: string) =>
    setSelectedFabrics((prev) =>
      prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]
    );

  const toggleSize = (s: string) =>
    setSelectedSizes((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );

  const { products: clothesProducts, loading } = useProducts({ category: "clothes" });

  const filtered = useMemo(() => {
    let list = [...clothesProducts];
    if (selectedFabrics.length > 0)
      list = list.filter((p) => selectedFabrics.includes(p.fabricLabel));
    if (selectedSizes.length > 0)
      list = list.filter((p) => p.sizes.some((s) => selectedSizes.includes(s)));
    list = list.filter((p) => p.price <= priceMax);
    switch (sortBy) {
      case "price_asc": list.sort((a, b) => a.price - b.price); break;
      case "price_desc": list.sort((a, b) => b.price - a.price); break;
      case "newest": list.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0)); break;
      case "rating": list.sort((a, b) => b.rating - a.rating); break;
      default: list.sort((a, b) => (b.isFeatured ? 1 : 0) - (a.isFeatured ? 1 : 0));
    }
    return list;
  }, [clothesProducts, selectedFabrics, selectedSizes, priceMax, sortBy]);

  const activeFilterCount = selectedFabrics.length + selectedSizes.length + (priceMax < 200 ? 1 : 0);

  return (
    <div className="min-h-screen bg-moonlight" style={{ fontFamily: "'Jost', sans-serif" }}>
      <Navbar />
      <CartSidebar />

      {/* Page Hero */}
      <section className="relative pt-20 lg:pt-24 overflow-hidden">
        <div className="relative h-56 md:h-72">
          <img
            src="https://readdy.ai/api/search-image?query=collection%20banner%20of%20multiple%20elegant%20women%20wearing%20lustrous%20satin%20and%20metallic%20dresses%20in%20a%20row%2C%20dark%20dramatic%20studio%20background%20with%20individual%20spotlights%2C%20luxury%20fashion%20campaign%20photography%2C%20silver%20and%20white%20tones%2C%20high%20fashion%20editorial&width=1440&height=400&seq=iif_clothes_banner&orientation=landscape"
            alt="i-clothes collection"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-midnight/70 via-midnight/40 to-midnight/30" />
          <div className="relative h-full flex flex-col justify-center px-8 lg:px-16">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-moonlight/50 text-[10px] tracking-wider uppercase mb-3">
              <Link to="/" className="hover:text-moonlight transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line text-xs" />
              <span className="text-moonlight/80">i-clothes</span>
            </div>
            <h1
              className="text-4xl md:text-5xl font-light italic text-moonlight"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              i-clothes
            </h1>
            <p className="text-xs tracking-widest uppercase text-moonlight/60 mt-1">
              Luminous Apparel — {clothesProducts.length} pieces
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-5 lg:px-16 py-10">
        {/* Toolbar */}
        <div className="flex items-center justify-between mb-7 pb-5 border-b border-pearl/40">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setFilterOpen((v) => !v)}
              className={`flex items-center gap-2 text-xs tracking-wider uppercase border px-4 py-2.5 transition-all cursor-pointer whitespace-nowrap ${
                filterOpen || activeFilterCount > 0
                  ? "border-midnight bg-midnight text-moonlight"
                  : "border-pearl text-midnight/60 hover:border-midnight hover:text-midnight"
              }`}
            >
              <i className="ri-filter-line" />
              Filter
              {activeFilterCount > 0 && (
                <span className="w-4 h-4 rounded-full bg-moon-gold-dark text-midnight text-[10px] flex items-center justify-center leading-none">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {activeFilterCount > 0 && (
              <button
                onClick={() => {
                  setSelectedFabrics([]);
                  setSelectedSizes([]);
                  setPriceMax(200);
                }}
                className="text-[10px] tracking-wider uppercase text-midnight/40 hover:text-midnight transition-colors cursor-pointer whitespace-nowrap"
              >
                Clear All
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-xs text-midnight/40">{filtered.length} results</span>
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-xs tracking-wide border border-pearl bg-transparent text-midnight px-3 py-2.5 pr-7 appearance-none cursor-pointer focus:outline-none focus:border-midnight"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-midnight/40 pointer-events-none text-sm" />
            </div>
          </div>
        </div>

        <div className="flex gap-8">
          {/* Filter Sidebar */}
          {filterOpen && (
            <aside className="hidden lg:block w-56 shrink-0">
              {/* Fabric */}
              <div className="mb-8">
                <h3 className="text-[10px] tracking-widest uppercase text-midnight/50 mb-4">Fabric Type</h3>
                <div className="space-y-2.5">
                  {FABRICS.map((f) => (
                    <label key={f} className="flex items-center gap-2.5 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={selectedFabrics.includes(f)}
                        onChange={() => toggleFabric(f)}
                        className="w-3.5 h-3.5 border-pearl accent-midnight cursor-pointer"
                      />
                      <span className="text-xs text-midnight/60 group-hover:text-midnight transition-colors">{f}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Size */}
              <div className="mb-8">
                <h3 className="text-[10px] tracking-widest uppercase text-midnight/50 mb-4">Size</h3>
                <div className="flex flex-wrap gap-2">
                  {SIZES.map((s) => (
                    <button
                      key={s}
                      onClick={() => toggleSize(s)}
                      className={`text-xs w-10 h-10 border transition-all cursor-pointer whitespace-nowrap ${
                        selectedSizes.includes(s)
                          ? "border-midnight bg-midnight text-moonlight"
                          : "border-pearl text-midnight/60 hover:border-midnight"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price */}
              <div>
                <h3 className="text-[10px] tracking-widest uppercase text-midnight/50 mb-4">
                  Max Price: <span className="text-midnight">${priceMax}</span>
                </h3>
                <input
                  type="range"
                  min={20}
                  max={200}
                  step={5}
                  value={priceMax}
                  onChange={(e) => setPriceMax(Number(e.target.value))}
                  className="w-full accent-midnight cursor-pointer"
                />
                <div className="flex justify-between text-[10px] text-midnight/30 mt-1">
                  <span>$20</span><span>$200</span>
                </div>
              </div>
            </aside>
          )}

          {/* Product Grid */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-5 lg:gap-7">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-pearl/30 aspect-[3/4] mb-3" />
                    <div className="bg-pearl/30 h-4 w-3/4 mb-2" />
                    <div className="bg-pearl/30 h-3 w-1/2" />
                  </div>
                ))}
              </div>
            ) : filtered.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-5 lg:gap-7">
                {filtered.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="text-center py-24">
                <i className="ri-search-line text-3xl text-midnight/20 mb-4 block" />
                <p className="text-sm text-midnight/40">No products match your filters.</p>
                <button
                  onClick={() => { setSelectedFabrics([]); setSelectedSizes([]); setPriceMax(200); }}
                  className="mt-4 text-xs tracking-widest uppercase text-midnight border-b border-midnight/30 pb-0.5 hover:border-midnight transition-colors cursor-pointer whitespace-nowrap"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
